import type { InternalChatActiveListItem, InternalChatCanal, InternalChatVinculo } from '../types';

export const isCanalInterno = (canal: InternalChatCanal | null | undefined): canal is 'INTERNO' =>
  canal === 'INTERNO';

type ProcessoVinculoSource = {
  canal?: InternalChatCanal | null;
  numeroProcesso?: string | null;
  vinculos?: InternalChatVinculo[];
};

/**
 * `entity_type` do vínculo tem mais de uma grafia em produção — o core normaliza a ESCRITA em
 * `ProcessoArbitral` (PascalCase) e mantém a classe de equivalência com `PROCESSO_ARBITRAL` para
 * a LEITURA, porque as linhas antigas não foram reescritas (ADR de normalização, D5).
 *
 * Comparar só com `PROCESSO_ARBITRAL` não casava nada no canal Interno: a conversa nasce do
 * `ensureConversaInternaDoProcesso`, que grava a canônica. O sintoma era o processo vir `null` —
 * o histórico rejeitava com "Conversa Interno sem processo arbitral vinculado" e a aba Andamentos
 * se auto-devolvia para Info.
 *
 * `tipo` vem primeiro porque é o enum `ConversaVinculoTipo`, que o core já serializa normalizado.
 */
const GRAFIAS_PROCESSO_ARBITRAL = ['ProcessoArbitral', 'PROCESSO_ARBITRAL'];

export const ehVinculoProcessoArbitral = (
  vinculo: Pick<InternalChatVinculo, 'entityType'> & { tipo?: string | null },
): boolean =>
  vinculo.tipo === 'PROCESSO_ARBITRAL' || GRAFIAS_PROCESSO_ARBITRAL.includes(vinculo.entityType);

export const resolveProcessoArbitralId = (
  item: ProcessoVinculoSource | null | undefined,
): string | null => {
  const arbitral = item?.vinculos?.find(ehVinculoProcessoArbitral);
  return arbitral?.entityId ?? null;
};

export const internoDisplayName = (item: InternalChatActiveListItem): string => {
  const numero = item.numeroProcesso?.trim();
  if (numero) return numero;
  // `vinculo.numero` existe em qualquer tipo de vínculo (notificação, negociação). Sem o filtro
  // por PROCESSO_ARBITRAL o título da conversa exibiria o número de outra entidade.
  const fromVinculo = item.vinculos
    .find((vinculo) => ehVinculoProcessoArbitral(vinculo) && vinculo.numero?.trim())
    ?.numero?.trim();
  if (fromVinculo) return fromVinculo;
  return item.nomeCliente?.trim() || 'Processo sem número';
};

export const conversaDisplayName = (item: InternalChatActiveListItem): string =>
  isCanalInterno(item.canal)
    ? internoDisplayName(item)
    : item.nomeCliente?.trim() || item.telefoneNormalizado;

export const buildRequeridosChips = (names: string[]): { visible: string[]; remaining: number } => {
  if (names.length === 0) return { visible: [], remaining: 0 };
  const [first, ...rest] = names;
  return { visible: first ? [first] : [], remaining: rest.length };
};

export const isOrigemEncerrada = (
  item: Pick<InternalChatActiveListItem, 'origemEncerrada'> | null | undefined,
): boolean => Boolean(item?.origemEncerrada);
